import React, { useState, useEffect } from 'react';
import "../../css/Classes.css";
import Loader from "../../components/Loader";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function TeacherClasses() {
    const [classes, setClasses] = useState([]);
    const [filteredClasses, setFilteredClasses] = useState([]);
    const [newClass, setNewClass] = useState({
        name: '',
        section: '',
        capacity: '',
        hod: ''
    });
    const [isLoading, setIsLoading] = useState(false);
    const [isFormVisible, setIsFormVisible] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [classToDelete, setClassToDelete] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchClasses();
    }, []);

    const fetchClasses = async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            toast.error("Unauthorized! Please log in again.");
            return;
        }

        setIsLoading(true);

        try {
            const response = await fetch("https://schoolpro-api.uniums.com/api/teacher/classes", {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch classes");
            }

            const data = await response.json();
            setClasses(data);
            setFilteredClasses(data);
        } catch (error) {
            console.error("Error fetching classes:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewClass({
            ...newClass,
            [name]: value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        const token = localStorage.getItem("token");

        try {
            let response;

            if (isEditing) {
                // Update existing class
                response = await fetch(`https://schoolpro-api.uniums.com/api/classes/${editingId}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify(newClass)
                });
            } else {
                // Add new class
                response = await fetch("https://schoolpro-api.uniums.com/api/classes/add", {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify(newClass)
                });
            }

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || (isEditing ? "Failed to update class" : "Failed to add class"));
            }

            const data = await response.json();

            if (isEditing) {
                toast.success("Class updated successfully!");
                setIsEditing(false);
                setEditingId(null);
            } else {
                toast.success("Class added successfully!");
            }

            // Refresh the class list
            fetchClasses();

            // Reset form
            setNewClass({
                name: '',
                section: '',
                capacity: '',
                hod: ''
            });
            setIsFormVisible(false);
        } catch (error) {
            console.error("Error:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

  

    const handleDelete = async () => {
        if (classToDelete) {
            setIsLoading(true);

            const token = localStorage.getItem("token");

            try {
                const response = await fetch(`https://schoolpro-api.uniums.com/api/classes/${classToDelete._id}`, {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || "Failed to delete class");
                }

                toast.success("Class deleted successfully!");
                fetchClasses();
            } catch (error) {
                console.error("Error deleting class:", error);
                toast.error(`Error: ${error.message}`);
            } finally {
                setIsLoading(false);
                setShowDeleteModal(false);
                setClassToDelete(null);
            }
        }
    };

    const cancelDelete = () => {
        setShowDeleteModal(false);
        setClassToDelete(null);
    };

    const cancelEdit = () => {
        setNewClass({
            name: '',
            section: '',
            capacity: '',
            hod: ''
        });
        setIsEditing(false);
        setEditingId(null);
        setIsFormVisible(false);
    };

    // Handle search
    useEffect(() => {
        if (searchTerm.trim() === '') {
            setFilteredClasses(classes);
        } else {
            const results = classes.filter(cls =>
                (cls.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                (cls.section?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                (cls.hod?.toLowerCase() || '').includes(searchTerm.toLowerCase())
            );
            setFilteredClasses(results);
        }
    }, [searchTerm, classes]);

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    return (
        <div className="class-container">
            <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} />

            {isLoading && <Loader />}

            <div className="class-header">
                <div className="class-title-container">
                    <h1 className="class-title">Class Management</h1>
                </div>


                <div className="class-stats">
                    <div className="stat-box">
                        <span className="stat-number">{filteredClasses.length}</span>
                        <span className="stat-label">Classes</span>
                    </div>
                </div>
            </div>

            {isFormVisible && (
                <div className="class-form-container">
                    <form className="class-form" onSubmit={handleSubmit}>
                        <h2 className="class-form-title">
                            {isEditing ? 'Edit Class' : 'Add New Class'}
                        </h2>

                        <div className="class-form-group">
                            <label className="class-label">Class Name*</label>
                            <input
                                type="text"
                                name="name"
                                value={newClass.name}
                                onChange={handleInputChange}
                                className="class-input"
                                placeholder="e.g. Grade 10"
                                required
                            />
                        </div>

                        <div className="class-form-group">
                            <label className="class-label">Section*</label>
                            <input
                                type="text"
                                name="section"
                                value={newClass.section}
                                onChange={handleInputChange}
                                className="class-input"
                                placeholder="e.g. A, B, Science"
                                required
                            />
                        </div>

                        <div className="class-form-group">
                            <label className="class-label">Capacity</label>
                            <input
                                type="number"
                                name="capacity"
                                value={newClass.capacity}
                                onChange={handleInputChange}
                                className="class-input"
                                placeholder="e.g. 30"
                            />
                        </div>

                        <div className="class-form-group">
                            <label className="class-label">HOD (Head of Department)</label>
                            <input
                                type="text"
                                name="hod"
                                value={newClass.hod}
                                onChange={handleInputChange}
                                className="class-input"
                                placeholder="e.g. Dr. Jane Smith"
                            />
                        </div>

                        <div className="class-form-actions">
                            <button type="submit" className="class-btn class-btn-success">
                                {isEditing ? 'Update Class' : 'Add Class'}
                            </button>
                            <button
                                type="button"
                                className="class-btn class-btn-secondary"
                                onClick={cancelEdit}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <div className="class-list-container">
                <h2 className="class-section-title">Existing Classes</h2>

                {filteredClasses.length === 0 && !isLoading ? (
                    <div className="class-empty-state">
                        {searchTerm ? (
                            <>
                                <p>No classes found matching your search criteria.</p>
                                <button className="reset-search" onClick={() => setSearchTerm('')}>
                                    Clear Search
                                </button>
                            </>
                        ) : (
                            <p>No classes have been added yet. Click "New Class" are not created.</p>
                        )}
                    </div>
                ) : (
                    <div className="class-grid">
                        {filteredClasses.map(cls => (
                            <div key={cls._id} className="class-card">
                                <div className="class-card-header">
                                    <h3 className="class-card-title">{cls.name} - {cls.section}</h3>
                                   
                                </div>
                                <div className="class-card-body">
                                    <p><strong>Capacity:</strong> {cls.capacity || 'Not specified'}</p>
                                    <p><strong>HOD:</strong> {cls.hod || 'Not assigned'}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

           
        </div>
    );
}

export default TeacherClasses;